/* eslint @ngrx/prefer-effect-callback-in-block-statement: off */
import { Injectable, inject } from '@angular/core';
import { Intercom } from '@intercom/messenger-js-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EMPTY, catchError, exhaustMap, filter, race, switchMap, take, tap, timer } from 'rxjs';

import { environment } from '@ninety/ui/web/environments';

import { FeatureFlagKeys } from '../../app-entities/feature-flag/feature-flag-state.model';
import { selectFeatureFlag } from '../../app-entities/feature-flag/feature-flag-state.selectors';
import { selectHasTier2Data } from '../../app-init/app-init.selectors';
import { appActions } from '../../app.actions';
import { INTERCOM_DEFER_MS } from '../third-party-scripts/third-party-defer';

import { FinApiService } from './fin-api.service';
import {
  AssessmentIntercomActions,
  BecomeAPartnerIntercomActions,
  DetailViewIntercomActions,
  MainLayoutIntercomActions,
  NavMenuIntercomActions,
  ReportViewerIntercomActions,
} from './intercom-state.actions';
import { IntercomRecord } from './intercom-state.selectors';

export const selectFinJwtValidation = selectFeatureFlag(FeatureFlagKeys.finJwtValidation);

@Injectable()
export class IntercomStateEffects {
  private actions$ = inject(Actions);
  private readonly store = inject(Store);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly window: any = window;

  private readonly finApiService = inject(FinApiService);

  /**
   * Defer Intercom initialization until after critical app data loads.
   *
   * Uses race(selectHasTier2Data, timer(INTERCOM_DEFER_MS)):
   * - On authenticated routes, Tier2 typically takes 3-4s so the
   *   timer wins — still moves Intercom past the early critical path.
   * - On login, Tier2 never loads so the timer is the effective trigger.
   */
  initializeIntercom$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.bootstrap),
        exhaustMap(() =>
          race(this.store.select(selectHasTier2Data).pipe(filter(Boolean), take(1)), timer(INTERCOM_DEFER_MS))
        ),
        tap(() => Intercom({ app_id: environment.INTERCOM_APP_ID }))
      ),
    { dispatch: false }
  );

  /**
   * Once Tier2 data is available the user is authenticated and their session is
   * established. Fetch the Fin JWT and pass it to Intercom so Fin can verify
   * the user's identity. Tier2 never loads on unauthenticated routes so this
   * effect is naturally a no-op on the login page.
   */
  updateIntercomWithFinJwt$ = createEffect(
    () =>
      this.store.select(selectHasTier2Data).pipe(
        filter(Boolean),
        take(1),
        concatLatestFrom(() => this.store.select(selectFinJwtValidation)),
        filter(([, flagEnabled]) => !!flagEnabled),
        switchMap(() =>
          this.finApiService.getFinToken().pipe(
            tap(({ access_token }: { access_token: string }) =>
              this._intercom('update', { intercom_user_jwt: access_token })
            ),
            catchError(() => EMPTY)
          )
        )
      ),
    { dispatch: false }
  );

  showIntercomLauncher$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AssessmentIntercomActions.showLauncher,
          ReportViewerIntercomActions.showIntercom,
          DetailViewIntercomActions.showIntercom,
          MainLayoutIntercomActions.showIntercom
        ),
        tap(_ => this._intercom('update', { hide_default_launcher: false }))
      ),
    { dispatch: false }
  );

  hideIntercomLauncher$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AssessmentIntercomActions.hideLauncher,
          ReportViewerIntercomActions.hideIntercom,
          DetailViewIntercomActions.hideIntercom,
          MainLayoutIntercomActions.hideIntercom
        ),
        tap(_ => this._intercom('update', { hide_default_launcher: true }))
      ),
    { dispatch: false }
  );

  showIntercomMessenger$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NavMenuIntercomActions.showMessenger, BecomeAPartnerIntercomActions.showMessenger),
        tap(_ => this._intercom('show'))
      ),
    { dispatch: false }
  );

  /** More Info: https://developers.intercom.com/installing-intercom/web/attributes-objects/ */
  private _intercom(action: string, config?: IntercomRecord) {
    // Sometimes window.Intercom doesn't exist yet or at all.
    if (!this.window?.Intercom) return;
    else if (!config) this.window.Intercom(action);
    else this.window.Intercom(action, config);
  }
}
